// Widget de accesibilidad — punto de entrada.
// Se compila a un solo archivo (public/widget.js) con esbuild.
// El cliente lo implementa con una sola línea:
//   <script src=".../widget.js" data-account="ID" data-api="..." defer></script>
//
// Diseño: panel tipo EqualWeb con perfiles, navegación, color y contenido.
// Todo el UI vive en Shadow DOM (aislado del sitio); los efectos sobre la página
// se aplican con atributos data-a11y-* + CSS (ver features.ts) y herramientas JS
// (ver tools.ts). Nunca debe romper el sitio anfitrión.

import { defaultConfig, fetchConfig, type WidgetConfig } from "./config";
import {
  PAGE_CSS,
  PROFILES,
  applyState,
  defaultState,
  type A11yState,
  type ColorMode,
  type Profile,
} from "./features";
import {
  Tools,
  showEasyRead,
  showPageStructure,
  showPageSummary,
  showReadableMode,
  type ToolContext,
  type ToolName,
} from "./tools";
import { getStrings, LANGS, LANG_NAMES, RTL, type Lang } from "./i18n";
import { icon } from "./icons";

// Se lee de forma SÍNCRONA al cargar el bundle (antes de cualquier await),
// porque document.currentScript se pierde dentro de callbacks asíncronos.
const scriptEl =
  (document.currentScript as HTMLScriptElement | null) ||
  (document.querySelector("script[data-account]") as HTMLScriptElement | null);

const accountId = scriptEl?.dataset.account ?? "demo";
const apiBase = scriptEl?.dataset.api ?? "http://localhost:8787";
const STORAGE_KEY = `a11y_state_${accountId}`;
const SIDE_KEY = `a11y_side_${accountId}`;
const HIDE_KEY = `a11y_hide_${accountId}`;

let state: A11yState = { ...defaultState };
let config: WidgetConfig = { ...defaultConfig };
let tools: Tools;
let shadow: ShadowRoot;
let host: HTMLElement;

const t = () => getStrings(state.lang);

function loadState(): A11yState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...defaultState, ...JSON.parse(raw) };
  } catch {
    /* localStorage bloqueado: valores por defecto */
  }
  return { ...defaultState };
}

function saveState(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignorar */
  }
}

// Sincroniza las herramientas JS con el estado (CSS ya lo hace applyState).
function applyTools(): void {
  const off = state.off;
  const map: [ToolName, boolean][] = [
    ["tts", state.tts],
    ["srMode", state.srMode],
    ["voice", state.voice],
    ["keyboardNav", state.keyboardNav],
    ["smartNav", state.smartNav],
    ["readGuide", state.readGuide],
    ["readFocus", state.readFocus],
    ["magnifier", state.magnifier],
    ["textMag", state.textMag],
    ["imgDesc", state.imgDesc],
    ["muteMedia", state.muteMedia],
    ["dict", state.dict],
    ["vkb", state.vkb],
  ];
  for (const [name, on] of map) tools.set(name, on && !off);
}

function update(): void {
  applyState(state);
  applyTools();
  saveState();
  refresh();
}

// ---------- definición del menú ----------

interface Tile {
  key: string;
  icon: string;
  label: () => string;
  isOn?: () => boolean;
  onClick: () => void;
}

// Si la herramienta estaba apagada, cualquier interacción la reactiva:
// un botón que "no hace nada" en silencio se percibe como roto.
function wake(): void {
  if (state.off) {
    state.off = false;
    render();
    (shadow.querySelector(".panel") as HTMLElement)?.classList.add("open");
  }
}

const toggle = (field: keyof A11yState) => () => {
  wake();
  (state as any)[field] = !(state as any)[field];
  update();
};

const setColorMode = (mode: ColorMode) => () => {
  wake();
  state.colorMode = state.colorMode === mode ? "none" : mode;
  update();
};

function applyProfile(p: Exclude<Profile, "">): void {
  wake();
  const lang = state.lang;
  const wasActive = state.profile === p;
  state = { ...defaultState, lang };
  if (!wasActive) {
    state = { ...state, ...PROFILES[p], profile: p };
  }
  update();
}

const PROFILE_TILES: Tile[] = (
  [
    ["motor", "pMotor"],
    ["blind", "pBlind"],
    ["colorblind", "pColorblind"],
    ["dyslexia", "pDyslexia"],
    ["lowvision", "pLowVision"],
    ["cognitive", "pCognitive"],
    ["epilepsy", "pEpilepsy"],
    ["adhd", "pAdhd"],
  ] as [Exclude<Profile, "">, string][]
).map(([key, label]) => ({
  key: `profile_${key}`,
  icon: key,
  label: () => t()[label],
  isOn: () => state.profile === key,
  onClick: () => applyProfile(key),
}));

const NAV_TILES: Tile[] = [
  { key: "srMode", icon: "srMode", label: () => t().srMode, isOn: () => state.srMode, onClick: toggle("srMode") },
  { key: "keyboardNav", icon: "keyboardNav", label: () => t().keyboardNav, isOn: () => state.keyboardNav, onClick: toggle("keyboardNav") },
  { key: "mousegrid", icon: "mousegrid", label: () => t().mousegrid, onClick: () => tools.mousegrid.start() },
  { key: "smartNav", icon: "smartNav", label: () => t().smartNav, isOn: () => state.smartNav, onClick: toggle("smartNav") },
  { key: "tts", icon: "tts", label: () => t().tts, isOn: () => state.tts, onClick: toggle("tts") },
  { key: "voice", icon: "voice", label: () => t().voice, isOn: () => state.voice, onClick: toggle("voice") },
];

const COLOR_TILES: Tile[] = [
  { key: "mono", icon: "mono", label: () => t().mono, isOn: () => state.colorMode === "mono", onClick: setColorMode("mono") },
  { key: "darkhc", icon: "darkhc", label: () => t().darkhc, isOn: () => state.colorMode === "darkhc", onClick: setColorMode("darkhc") },
  { key: "brighthc", icon: "brighthc", label: () => t().brighthc, isOn: () => state.colorMode === "brighthc", onClick: setColorMode("brighthc") },
  { key: "lowsat", icon: "lowsat", label: () => t().lowsat, isOn: () => state.colorMode === "lowsat", onClick: setColorMode("lowsat") },
  { key: "highsat", icon: "highsat", label: () => t().highsat, isOn: () => state.colorMode === "highsat", onClick: setColorMode("highsat") },
  { key: "contrastMode", icon: "contrastMode", label: () => t().contrastMode, isOn: () => state.colorMode === "contrast", onClick: setColorMode("contrast") },
];

const CONTENT_TILES: Tile[] = [
  { key: "blinks", icon: "blinks", label: () => t().blinks, isOn: () => state.blinks, onClick: toggle("blinks") },
  { key: "magnifier", icon: "magnifier", label: () => t().magnifier, isOn: () => state.magnifier, onClick: toggle("magnifier") },
  { key: "readableFont", icon: "readableFont", label: () => t().readableFont, isOn: () => state.readableFont, onClick: toggle("readableFont") },
  { key: "imgDesc", icon: "imgDesc", label: () => t().imgDesc, isOn: () => state.imgDesc, onClick: toggle("imgDesc") },
  { key: "links", icon: "links", label: () => t().links, isOn: () => state.links, onClick: toggle("links") },
  { key: "readableMode", icon: "readableMode", label: () => t().readableMode, onClick: () => showReadableMode(toolCtx()) },
  { key: "textMag", icon: "textMag", label: () => t().textMag, isOn: () => state.textMag, onClick: toggle("textMag") },
  { key: "pageStructure", icon: "pageStructure", label: () => t().pageStructure, onClick: () => showPageStructure(toolCtx()) },
  { key: "muteMedia", icon: "muteMedia", label: () => t().muteMedia, isOn: () => state.muteMedia, onClick: toggle("muteMedia") },
  { key: "pageSummary", icon: "pageSummary", label: () => t().pageSummary, onClick: () => showPageSummary(toolCtx()) },
  { key: "readFocus", icon: "readFocus", label: () => t().readFocus, isOn: () => state.readFocus, onClick: toggle("readFocus") },
  { key: "readGuide", icon: "readGuide", label: () => t().readGuide, isOn: () => state.readGuide, onClick: toggle("readGuide") },
  { key: "dict", icon: "dict", label: () => t().dict, isOn: () => state.dict, onClick: toggle("dict") },
  { key: "vkb", icon: "vkb", label: () => t().vkb, isOn: () => state.vkb, onClick: toggle("vkb") },
  { key: "easyRead", icon: "easyRead", label: () => t().easyRead, onClick: () => showEasyRead(toolCtx()) },
];

let toolCtxCache: ToolContext | null = null;
function toolCtx(): ToolContext {
  if (!toolCtxCache) {
    toolCtxCache = {
      shadow,
      host,
      t,
      lang: () => state.lang,
      color: config.color,
      api: apiBase,
    };
  }
  return toolCtxCache;
}

// ---------- render ----------

function widgetCSS(side: string): string {
  const c = config.color;
  return `
  * { box-sizing: border-box; font-family: system-ui, -apple-system, "Segoe UI", sans-serif; margin: 0; }
  button { font: inherit; }

  .btn {
    position: fixed; bottom: 20px; ${side}: 20px; z-index: 2147483647;
    width: 56px; height: 56px; border-radius: 50%; border: 3px solid #fff;
    background: ${c}; color: #fff; cursor: pointer;
    box-shadow: 0 4px 14px rgba(0,0,0,.3); font-size: 26px; line-height: 1;
    display: flex; align-items: center; justify-content: center;
  }
  .btn:focus-visible { outline: 3px solid #111; outline-offset: 2px; }

  .panel {
    position: fixed; top: 10px; bottom: 10px; ${side}: 10px; z-index: 2147483647;
    width: 384px; max-width: calc(100vw - 20px); background: #f5f8fc; color: #14324f;
    box-shadow: 0 12px 50px rgba(10,30,60,.35); display: none; flex-direction: column;
    border-radius: 20px; overflow: hidden;
  }
  .panel.open { display: flex; }

  .head {
    background: ${c}; color: #fff; padding: 10px 14px;
    display: flex; align-items: center; gap: 8px;
  }
  .head .title { font-weight: 700; font-size: 17px; flex: 1; text-align: center; }
  .head button {
    background: rgba(255,255,255,.15); border: none; color: #fff; font-size: 16px;
    cursor: pointer; line-height: 1; border-radius: 6px; padding: 6px 8px;
    display: inline-flex; align-items: center; justify-content: center;
  }
  .head button svg { width: 16px; height: 16px; }
  .ic svg { width: 24px; height: 24px; display: block; }
  .head button:hover { background: rgba(255,255,255,.3); }
  .head select {
    background: rgba(255,255,255,.2); color: #fff; border: 1px solid rgba(255,255,255,.5);
    border-radius: 999px; padding: 4px 8px; font-size: 12px; cursor: pointer;
  }
  .head select option { color: #14324f; }

  .body { padding: 12px; overflow-y: auto; flex: 1; }

  .off-note {
    background: #fef3c7; color: #78350f; border: 2px solid #f59e0b;
    border-radius: 14px; padding: 12px 14px; font-size: 13.5px; font-weight: 600;
    margin-bottom: 12px;
  }

  .section { margin-bottom: 16px; }
  .section-head {
    display: flex; justify-content: space-between; align-items: center;
    color: ${c}; font-weight: 700; font-size: 13.5px; margin-bottom: 10px;
    text-transform: uppercase; letter-spacing: .02em;
  }
  .section-head button {
    background: ${c}; color: #fff; border: none; width: 24px; height: 24px;
    border-radius: 8px; cursor: pointer; font-size: 15px; line-height: 1;
  }
  .section-body.closed { display: none; }

  .grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; }
  .tile {
    border: 1px solid #e2eaf3; background: #fff; border-radius: 16px;
    padding: 14px 8px 12px; font-size: 11.5px; cursor: pointer; color: #14324f;
    text-align: center; transition: border-color .15s, box-shadow .15s, transform .1s;
    display: flex; flex-direction: column; align-items: center; gap: 9px; min-height: 92px;
    justify-content: center; box-shadow: 0 1px 3px rgba(20,50,79,.06);
  }
  .tile .ic { color: ${c}; }
  .tile:hover { border-color: ${c}; box-shadow: 0 4px 12px rgba(20,50,79,.12); transform: translateY(-1px); }
  .tile:focus-visible { outline: 3px solid ${c}; outline-offset: 2px; }
  .tile.active { background: ${c}; color: #fff; border-color: ${c}; box-shadow: 0 4px 14px rgba(20,50,79,.25); }
  .tile.active .ic { color: #fff; }

  .card {
    border: 1px solid #e2eaf3; background: #fff; border-radius: 16px;
    padding: 14px; margin-top: 10px; box-shadow: 0 1px 3px rgba(20,50,79,.06);
  }
  .card .card-title { font-weight: 700; font-size: 13px; }
  .card .card-desc { font-size: 11px; color: #64748b; margin-bottom: 8px; }
  .pills { display: flex; gap: 6px; margin-bottom: 12px; flex-wrap: wrap; }
  .pills button {
    flex: 1; border: 1px solid #cfd9e4; background: #fff; color: #14324f;
    border-radius: 999px; padding: 7px 10px; font-size: 11.5px; cursor: pointer;
    white-space: nowrap; transition: .15s;
  }
  .pills button:hover { border-color: ${c}; }
  .pills button.on { background: ${c}; color: #fff; border-color: ${c}; }

  input[type=range] { width: 100%; cursor: pointer; accent-color: ${c}; }
  .hue { -webkit-appearance: none; appearance: none; height: 12px; border-radius: 999px;
    background: linear-gradient(to right, hsl(0,80%,55%), hsl(60,80%,55%), hsl(120,80%,45%),
      hsl(180,80%,45%), hsl(240,80%,55%), hsl(300,80%,55%), hsl(360,80%,55%)); }

  .linkbtn {
    background: none; border: none; color: ${c}; cursor: pointer; font-size: 12px;
    text-decoration: underline; padding: 4px 0; display: inline-flex; align-items: center; gap: 4px;
  }

  .cursor-row { display: flex; gap: 8px; }
  .cursor-row button {
    flex: 1; border: 1px solid #cfd9e4; border-radius: 999px; background: #fff;
    padding: 8px; cursor: pointer; font-size: 12px; font-weight: 600; color: #14324f;
  }
  .cursor-row button.on { background: ${c}; color: #fff; border-color: ${c}; }

  .foot {
    background: ${c}; color: #fff; padding: 10px 12px; display: flex;
    justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap;
  }
  .foot button, .foot a {
    background: none; border: none; color: #fff; cursor: pointer; font-size: 12.5px;
    text-decoration: none; padding: 4px;
  }
  .foot button:hover, .foot a:hover { text-decoration: underline; }
  .powered { font-size: 10px; text-align: center; color: #8aa2b8; padding: 4px; background: #f1f5f9; }

  /* ----- elementos de herramientas (overlays) ----- */
  .a11y-chip {
    position: fixed; bottom: 90px; left: 50%; transform: translateX(-50%);
    background: #14324f; color: #fff; padding: 10px 16px; border-radius: 999px;
    font-size: 13px; z-index: 2147483647; box-shadow: 0 4px 14px rgba(0,0,0,.3);
    display: none; max-width: 90vw; text-align: center;
  }
  .a11y-guide {
    position: fixed; left: 0; right: 0; top: 40%; height: 10px; border-radius: 5px;
    background: #f59e0b; opacity: .85; z-index: 2147483646; pointer-events: none;
    box-shadow: 0 0 0 2px rgba(0,0,0,.25);
  }
  .a11y-mask { position: fixed; left: 0; right: 0; background: rgba(0,0,0,.55); z-index: 2147483645; pointer-events: none; }
  .a11y-mask-top { top: 0; height: 30vh; }
  .a11y-mask-bottom { top: 60vh; bottom: 0; }
  .a11y-lens {
    position: fixed; z-index: 2147483646; width: 320px; max-height: 150px; overflow: hidden;
    background: #fff; color: #111; border: 3px solid ${c}; border-radius: 14px;
    padding: 10px 14px; font-size: 20px; font-weight: 600; line-height: 1.35;
    box-shadow: 0 8px 30px rgba(0,0,0,.35); pointer-events: none; display: none;
  }
  .a11y-textmag {
    position: fixed; left: 0; right: 0; bottom: 0; z-index: 2147483646;
    background: #14324f; color: #fff; font-size: 26px; font-weight: 600; line-height: 1.4;
    padding: 14px 20px; max-height: 30vh; overflow: hidden; display: none; pointer-events: none;
  }
  .a11y-imgdesc-layer { position: fixed; inset: 0; z-index: 2147483644; pointer-events: none; }
  .a11y-imgdesc {
    position: absolute; background: rgba(20,50,79,.92); color: #fff; font-size: 12px;
    padding: 4px 8px; border-radius: 6px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .a11y-imgdesc.missing { background: rgba(190,18,60,.92); }
  .a11y-grid { position: fixed; inset: 0; z-index: 2147483647; }
  .a11y-grid-cell {
    position: fixed; border: 1px dashed rgba(255,255,255,.9); background: rgba(20,50,79,.25);
    color: #fff; font-size: 28px; font-weight: 700; display: flex; align-items: center;
    justify-content: center; text-shadow: 0 1px 3px #000;
  }
  .a11y-grid-hint {
    position: fixed; bottom: 12px; left: 50%; transform: translateX(-50%);
    background: #14324f; color: #fff; padding: 8px 14px; border-radius: 999px; font-size: 13px;
  }
  .a11y-dict {
    position: fixed; z-index: 2147483647; width: 320px; background: #fff; color: #14324f;
    border: 2px solid ${c}; border-radius: 12px; padding: 12px; font-size: 13px;
    box-shadow: 0 8px 30px rgba(0,0,0,.3);
  }
  .a11y-dict strong { display: block; margin-bottom: 6px; text-transform: capitalize; }
  .a11y-vkb {
    position: fixed; bottom: 8px; left: 50%; transform: translateX(-50%);
    background: #14324f; border-radius: 14px; padding: 8px; z-index: 2147483647;
    box-shadow: 0 8px 30px rgba(0,0,0,.4); max-width: 96vw;
  }
  .a11y-vkb-row { display: flex; gap: 4px; justify-content: center; margin-bottom: 4px; }
  .a11y-vkb button {
    min-width: 32px; height: 38px; border: none; border-radius: 7px; background: #2c5075;
    color: #fff; font-size: 15px; cursor: pointer; padding: 0 8px;
  }
  .a11y-vkb button:hover { background: ${c}; }
  .a11y-vkb button.wide { min-width: 140px; }
  .a11y-vkb.shift button { text-transform: uppercase; }
  .a11y-dialog {
    position: fixed; inset: 0; z-index: 2147483647; background: rgba(0,0,0,.5);
    display: flex; align-items: center; justify-content: center; padding: 16px;
  }
  .a11y-dialog-box {
    background: #fff; color: #14324f; border-radius: 14px; width: 560px; max-width: 94vw;
    max-height: 84vh; display: flex; flex-direction: column; overflow: hidden;
    box-shadow: 0 20px 60px rgba(0,0,0,.4);
  }
  .a11y-dialog-head {
    background: ${c}; color: #fff; padding: 12px 16px; font-weight: 700;
    display: flex; justify-content: space-between; align-items: center;
  }
  .a11y-dialog-head button { background: none; border: none; color: #fff; font-size: 22px; cursor: pointer; }
  .a11y-dialog-body { padding: 14px; overflow-y: auto; font-size: 14px; line-height: 1.6; }
  .a11y-tabs { display: flex; gap: 6px; margin-bottom: 10px; }
  .a11y-tabs button {
    border: 1px solid #cfd9e4; background: #fff; border-radius: 999px; padding: 6px 12px;
    cursor: pointer; font-size: 12.5px; color: #14324f;
  }
  .a11y-tabs button.on { background: ${c}; color: #fff; border-color: ${c}; }
  .a11y-list { display: flex; flex-direction: column; gap: 4px; }
  .a11y-list button {
    text-align: left; border: 1px solid #e2e8f0; background: #f8fafc; color: #14324f;
    border-radius: 8px; padding: 8px 10px; cursor: pointer; font-size: 13px;
  }
  .a11y-list button:hover { border-color: ${c}; }
  .a11y-empty { color: #64748b; }
  .a11y-summary div { margin-bottom: 12px; }
  .a11y-summary p { color: #475569; font-size: 13px; }
  .a11y-aibox {
    margin-top: 14px; border: 1px solid #bfdbfe; background: #eff6ff;
    border-radius: 14px; padding: 12px 14px;
  }
  .a11y-aibox strong { display: block; margin-bottom: 6px; color: ${c}; }
  .a11y-aibox p { white-space: pre-wrap; font-size: 13.5px; }
  .a11y-ailoading { color: #64748b; font-style: italic; }
  .a11y-readable-view { font-family: Georgia, "Times New Roman", serif; font-size: 19px; }
  .a11y-readable-view h1, .a11y-readable-view h2, .a11y-readable-view h3 { margin: 14px 0 6px; }
  .a11y-readable-view p { margin-bottom: 10px; }

  @media (max-width: 480px) { .panel { width: 100vw; } }
  @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
  `;
}

function tileButton(tile: Tile): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "tile";
  b.dataset.key = tile.key;
  b.innerHTML = `<span class="ic" aria-hidden="true">${icon(tile.icon)}</span><span>${tile.label()}</span>`;
  if (tile.isOn) b.setAttribute("aria-pressed", String(tile.isOn()));
  b.addEventListener("click", tile.onClick);
  return b;
}

function section(
  title: string,
  build: (body: HTMLElement) => void
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "section";
  const head = document.createElement("div");
  head.className = "section-head";
  const label = document.createElement("span");
  label.textContent = title;
  const btn = document.createElement("button");
  btn.type = "button";
  btn.textContent = "−";
  btn.setAttribute("aria-expanded", "true");
  const body = document.createElement("div");
  body.className = "section-body";
  btn.addEventListener("click", () => {
    const closed = body.classList.toggle("closed");
    btn.textContent = closed ? "+" : "−";
    btn.setAttribute("aria-expanded", String(!closed));
  });
  head.appendChild(label);
  head.appendChild(btn);
  wrap.appendChild(head);
  wrap.appendChild(body);
  build(body);
  return wrap;
}

function grid(tiles: Tile[]): HTMLElement {
  const g = document.createElement("div");
  g.className = "grid";
  const allowed = config.features;
  for (const tile of tiles) {
    if (allowed.length && !allowed.includes(tile.key) && !tile.key.startsWith("profile_"))
      continue;
    g.appendChild(tileButton(tile));
  }
  return g;
}

// Tarjeta con pestañas + deslizador (tamaño de fuente / espaciados y colores).
function sliderCard(opts: {
  title: string;
  desc: string;
  tabs: { label: string; get: () => number; set: (v: number) => void; max: number }[];
  resetLabel: string;
  onReset: () => void;
  hue?: boolean;
}): HTMLElement {
  const card = document.createElement("div");
  card.className = "card";
  card.innerHTML = `<div class="card-title">${opts.title}</div><div class="card-desc">${opts.desc}</div>`;
  const pills = document.createElement("div");
  pills.className = "pills";
  const range = document.createElement("input");
  range.type = "range";
  if (opts.hue) range.className = "hue";
  let current = 0;
  const select = (i: number) => {
    current = i;
    pills.querySelectorAll("button").forEach((b, j) => b.classList.toggle("on", j === i));
    range.min = opts.hue ? "-1" : "0";
    range.max = String(opts.tabs[i].max);
    range.value = String(opts.tabs[i].get());
  };
  opts.tabs.forEach((tab, i) => {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = tab.label;
    b.addEventListener("click", () => select(i));
    pills.appendChild(b);
  });
  range.addEventListener("input", () => {
    wake();
    opts.tabs[current].set(parseInt(range.value, 10));
    update();
  });
  const reset = document.createElement("button");
  reset.type = "button";
  reset.className = "linkbtn";
  reset.textContent = "↺ " + opts.resetLabel;
  reset.addEventListener("click", () => {
    opts.onReset();
    update();
    select(current);
  });
  card.appendChild(pills);
  card.appendChild(range);
  card.appendChild(reset);
  select(0);
  return card;
}

function render(): void {
  const side = getSide();
  const s = t();

  shadow.innerHTML = `<style>${widgetCSS(side)}</style>`;

  // Botón flotante
  const btn = document.createElement("button");
  btn.className = "btn";
  btn.type = "button";
  btn.setAttribute("aria-label", s.open);
  btn.innerHTML = `<svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm9 5.5c.1.5-.2 1-.7 1.2l-4.8 1.1v2.5l2.4 6.4c.2.5-.05 1.1-.55 1.3-.5.2-1.1-.05-1.3-.55L12.9 14h-1.8l-3.15 5.45c-.2.5-.8.75-1.3.55-.5-.2-.75-.8-.55-1.3L8.5 12.3V9.8L3.7 8.7c-.5-.2-.8-.7-.7-1.2.2-.5.7-.8 1.2-.7l5.6 1.3h4.4l5.6-1.3c.5-.1 1 .2 1.2.7z"/></svg>`;
  btn.addEventListener("click", () => {
    panel.classList.toggle("open");
    if (panel.classList.contains("open"))
      (panel.querySelector(".head .close") as HTMLElement)?.focus();
  });
  shadow.appendChild(btn);

  // Panel
  const panel = document.createElement("div");
  panel.className = "panel";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-label", s.title);
  panel.dir = RTL.includes(state.lang) ? "rtl" : "ltr";
  shadow.appendChild(panel);

  // Cabecera
  const head = document.createElement("div");
  head.className = "head";
  head.innerHTML = `
    <button type="button" class="move" title="${s.moveSide}" aria-label="${s.moveSide}">${icon("move")}</button>
    <button type="button" class="hide" title="${s.hideWidget}" aria-label="${s.hideWidget}">${icon("hide")}</button>
    <span class="title">${s.title}</span>
    <select class="lang" aria-label="Language">
      ${LANGS.map(
        (l) =>
          `<option value="${l}"${state.lang === l ? " selected" : ""}>${LANG_NAMES[l]}</option>`
      ).join("")}
    </select>
    <button type="button" class="close" aria-label="${s.close}">${icon("close")}</button>`;
  panel.appendChild(head);
  (head.querySelector(".close") as HTMLElement).addEventListener("click", () =>
    panel.classList.remove("open")
  );
  (head.querySelector(".move") as HTMLElement).addEventListener("click", () => {
    setSide(side === "right" ? "left" : "right");
    render();
    (shadow.querySelector(".panel") as HTMLElement).classList.add("open");
  });
  (head.querySelector(".hide") as HTMLElement).addEventListener("click", () => {
    try {
      sessionStorage.setItem(HIDE_KEY, "1");
    } catch {
      /* ignorar */
    }
    host.style.display = "none";
  });
  (head.querySelector(".lang") as HTMLSelectElement).addEventListener("change", (e) => {
    state.lang = (e.target as HTMLSelectElement).value as Lang;
    saveState();
    render();
    (shadow.querySelector(".panel") as HTMLElement).classList.add("open");
  });

  // Cuerpo
  const body = document.createElement("div");
  body.className = "body";
  panel.appendChild(body);

  if (state.off) {
    const note = document.createElement("div");
    note.className = "off-note";
    note.textContent = s.offNote;
    body.appendChild(note);
  }

  body.appendChild(section(s.profiles, (el) => el.appendChild(grid(PROFILE_TILES))));

  body.appendChild(section(s.navigation, (el) => el.appendChild(grid(NAV_TILES))));

  body.appendChild(
    section(s.colors, (el) => {
      el.appendChild(grid(COLOR_TILES));
      el.appendChild(
        sliderCard({
          title: s.customColor,
          desc: s.customColorDesc,
          hue: true,
          tabs: [
            { label: s.bgs, get: () => state.bgHue, set: (v) => (state.bgHue = v), max: 360 },
            { label: s.headings, get: () => state.headHue, set: (v) => (state.headHue = v), max: 360 },
            { label: s.contents, get: () => state.textHue, set: (v) => (state.textHue = v), max: 360 },
          ],
          resetLabel: s.resetColors,
          onReset: () => {
            state.bgHue = state.headHue = state.textHue = -1;
            state.colorMode = "none";
          },
        })
      );
    })
  );

  body.appendChild(
    section(s.content, (el) => {
      el.appendChild(
        sliderCard({
          title: s.fontSizing,
          desc: s.fontSizingDesc,
          tabs: [
            { label: s.fontSize, get: () => state.fontSize, set: (v) => (state.fontSize = v), max: 8 },
            { label: s.lineSp, get: () => state.lineSp, set: (v) => (state.lineSp = v), max: 4 },
            { label: s.wordSp, get: () => state.wordSp, set: (v) => (state.wordSp = v), max: 4 },
            { label: s.letterSp, get: () => state.letterSp, set: (v) => (state.letterSp = v), max: 4 },
          ],
          resetLabel: s.resetFont,
          onReset: () => {
            state.fontSize = state.lineSp = state.wordSp = state.letterSp = 0;
            state.readableFont = false;
          },
        })
      );

      const cursorCard = document.createElement("div");
      cursorCard.className = "card";
      cursorCard.innerHTML = `<div class="card-title">${s.cursor}</div><div class="card-desc">${s.cursorDesc}</div>`;
      const row = document.createElement("div");
      row.className = "cursor-row";
      for (const mode of ["white", "black"] as const) {
        const b = document.createElement("button");
        b.type = "button";
        b.dataset.cursor = mode;
        b.textContent = mode === "white" ? s.cursorWhite : s.cursorBlack;
        b.addEventListener("click", () => {
          wake();
          state.cursor = state.cursor === mode ? "none" : mode;
          update();
        });
        row.appendChild(b);
      }
      cursorCard.appendChild(row);
      el.appendChild(cursorCard);

      const g = grid(CONTENT_TILES);
      g.style.marginTop = "8px";
      el.appendChild(g);
    })
  );

  // Pie
  const foot = document.createElement("div");
  foot.className = "foot";
  const offBtn = document.createElement("button");
  offBtn.type = "button";
  offBtn.textContent = state.off ? s.turnOn : s.turnOff;
  offBtn.addEventListener("click", () => {
    state.off = !state.off;
    update();
    render();
    (shadow.querySelector(".panel") as HTMLElement).classList.add("open");
  });
  const stmtBtn = document.createElement("button");
  stmtBtn.type = "button";
  stmtBtn.textContent = s.statement;
  stmtBtn.addEventListener("click", () => {
    const dlg = document.createElement("div");
    dlg.className = "a11y-dialog";
    dlg.innerHTML = `<div class="a11y-dialog-box" role="dialog" aria-label="${s.statement}">
      <div class="a11y-dialog-head"><span>${s.statement}</span><button type="button" aria-label="${s.close}">&times;</button></div>
      <div class="a11y-dialog-body"><p>${s.statementText}</p></div></div>`;
    dlg.addEventListener("click", (e) => e.target === dlg && dlg.remove());
    (dlg.querySelector("button") as HTMLElement).addEventListener("click", () => dlg.remove());
    shadow.appendChild(dlg);
  });
  const fbLink = document.createElement("a");
  fbLink.href = `mailto:${config.feedbackEmail}?subject=${encodeURIComponent("Accesibilidad: " + location.hostname)}`;
  fbLink.textContent = s.feedback;
  const resetBtn = document.createElement("button");
  resetBtn.type = "button";
  resetBtn.textContent = "↺ " + s.resetAll;
  resetBtn.addEventListener("click", () => {
    const lang = state.lang;
    state = { ...defaultState, lang };
    tools.stopAll();
    update();
    render();
    (shadow.querySelector(".panel") as HTMLElement).classList.add("open");
  });
  foot.appendChild(offBtn);
  foot.appendChild(stmtBtn);
  foot.appendChild(fbLink);
  foot.appendChild(resetBtn);
  panel.appendChild(foot);

  const powered = document.createElement("div");
  powered.className = "powered";
  powered.textContent = "Powered by Alcance";
  panel.appendChild(powered);

  refresh();
}

// Marca en el panel qué funciones están activas (aria-pressed + estilo).
function refresh(): void {
  const all = [...PROFILE_TILES, ...NAV_TILES, ...COLOR_TILES, ...CONTENT_TILES];
  shadow.querySelectorAll<HTMLElement>(".tile").forEach((el) => {
    const tile = all.find((x) => x.key === el.dataset.key);
    if (tile?.isOn) {
      const on = tile.isOn() && !state.off;
      el.classList.toggle("active", on);
      el.setAttribute("aria-pressed", String(on));
    }
  });
  shadow.querySelectorAll<HTMLElement>(".cursor-row button").forEach((el) => {
    el.classList.toggle("on", state.cursor === el.dataset.cursor && !state.off);
  });
}

function getSide(): "left" | "right" {
  try {
    const saved = localStorage.getItem(SIDE_KEY);
    if (saved === "left" || saved === "right") return saved;
  } catch {
    /* ignorar */
  }
  return config.position === "left" ? "left" : "right";
}

function setSide(side: "left" | "right"): void {
  try {
    localStorage.setItem(SIDE_KEY, side);
  } catch {
    /* ignorar */
  }
}

// ---------- arranque ----------

async function init(): Promise<void> {
  try {
    if (sessionStorage.getItem(HIDE_KEY)) return;
  } catch {
    /* ignorar */
  }

  state = loadState();

  // 1. Reglas que transforman la PÁGINA del cliente (van en el <head> del sitio).
  const pageStyle = document.createElement("style");
  pageStyle.id = "a11y-page-css";
  pageStyle.textContent = PAGE_CSS;
  document.head.appendChild(pageStyle);

  // 2. Config del cliente (color, posición, idioma, funciones habilitadas).
  //    Si el endpoint falla, arrancamos con los valores por defecto.
  config = await fetchConfig(apiBase, accountId).catch(() => ({ ...defaultConfig }));
  try {
    if (!localStorage.getItem(STORAGE_KEY) && LANGS.includes(config.language as Lang)) {
      state.lang = config.language as Lang;
    }
  } catch {
    /* localStorage bloqueado */
  }

  // 3. UI en Shadow DOM, colgado de <html> (no de <body>) para que los filtros
  //    de color aplicados a body no rompan el position:fixed del widget.
  host = document.createElement("div");
  host.id = "a11y-widget-root";
  document.documentElement.appendChild(host);
  shadow = host.attachShadow({ mode: "open" });

  tools = new Tools(toolCtx());

  render();

  // WCAG: Escape cierra diálogos y panel, devolviendo el foco al botón flotante.
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    const dlg = shadow.querySelector(".a11y-dialog");
    if (dlg) {
      dlg.remove();
      return;
    }
    if (shadow.querySelector(".a11y-grid")) return; // la cuadrícula gestiona su propio Escape
    const panel = shadow.querySelector(".panel");
    if (panel?.classList.contains("open")) {
      panel.classList.remove("open");
      (shadow.querySelector(".btn") as HTMLElement)?.focus();
    }
  });

  // Aplicar lo que el visitante guardó en visitas anteriores.
  applyState(state);
  applyTools();
}

// Nunca debe romper la página del cliente: todo envuelto en try/catch.
function boot(): void {
  init().catch((err) => console.warn("[a11y-widget]", err));
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
